// v0.7 side bets between real players: a horse in today's race, or a week-long showdown.
// The game holds both stakes until the bet is decided, so nobody can welsh.
import { useEffect, useState } from 'react';
import { api } from '../api.ts';
import { useGame, money, fmt, duration } from '../state.tsx';
import { Loading, Panel, useLoad, PlayerLink } from '../components/ui.tsx';
import { Portrait } from '../art/Portrait.tsx';
import { CasinoNav } from './Casino.tsx';
import { Silks } from './Races.tsx';
import { play, sequence } from '../fx/sound.ts';
import { fx } from '../fx/fx.tsx';

const SEEN = 'mm-sidebets-seen';
function seenIds(): Set<number> {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN) ?? '[]')); } catch { return new Set(); }
}
function markSeen(ids: number[]) {
  try { localStorage.setItem(SEEN, JSON.stringify(ids.slice(-60))); } catch { /* ignore */ }
}

function HorsePicker({ horses, value, onChange, taken }: { horses: any[]; value: number | null; onChange: (i: number) => void; taken?: number | null }) {
  return (
    <div className="horse-picker">
      {horses.map((h: any, i: number) => (
        <label key={i} className={`${value === i ? 'on' : ''} ${taken === i ? 'taken' : ''}`} title={taken === i ? 'Their horse' : ''}>
          <input type="radio" name="sb-horse" checked={value === i} disabled={taken === i} onChange={() => onChange(i)} />
          <Silks color={h.silks} size={14} /> {h.name}{taken === i && <span className="small dim"> (theirs)</span>}
        </label>
      ))}
    </div>
  );
}

export function SideBetsPage({ arg }: { arg?: string }) {
  const { me, act, content } = useGame();
  const { data, reload } = useLoad(() => api.get('/api/sidebets'), [me?.cash], 30_000);
  const people = useLoad(() => api.get('/api/chat/people'), []);
  const preset = arg?.startsWith('new-') ? Number(arg.slice(4)) : null;
  const [opponent, setOpponent] = useState<number | null>(preset);
  const [kind, setKind] = useState<'race' | 'showdown'>('showdown');
  const [stat, setStat] = useState('cash_earned');
  const [amount, setAmount] = useState('');
  const [horse, setHorse] = useState<number | null>(null);
  const [answerHorse, setAnswerHorse] = useState<Record<number, number>>({});
  useEffect(() => { if (preset) setOpponent(preset); }, [preset]);

  // Celebrate bets that were settled since you last looked.
  useEffect(() => {
    if (!data) return;
    const seen = seenIds();
    const fresh = data.bets.filter((b: any) => b.status === 'settled' && !seen.has(b.id));
    if (fresh.length && seen.size > 0) {
      const won = fresh.filter((b: any) => b.won === true);
      if (won.length) {
        sequence([[0, 'chipSlide'], [300, 'win']]);
        fx.coins(18);
        fx.stamp('Bet won', `+${money(won.reduce((s: number, b: any) => s + b.amount * 2, 0))} from ${won.map((b: any) => b.other.name).join(', ')}`, 'good');
      } else if (fresh.some((b: any) => b.won === false)) play('lose');
    }
    markSeen(data.bets.filter((b: any) => b.status === 'settled').map((b: any) => b.id).concat([...seen]));
  }, [data]);

  if (!data || !people.data) return <div><CasinoNav game="sidebets" /><Loading /></div>;
  const rules = content.rules.sidebets;
  const others = people.data.players.filter((p: any) => p.id !== me.id);
  const n = Math.floor(Number(amount.replace(/[^0-9]/g, '')));
  const raceSoon = data.race.postTime - Date.now() < 5 * 60_000;
  const challenge = async () => {
    const res = await act('/api/sidebets/challenge', { opponentId: opponent, kind, amount: n, horse: kind === 'race' ? horse : undefined, stat: kind === 'showdown' ? stat : undefined });
    if (res) {
      play('envelope');
      fx.float(`−${money(n)} held`, 'loss');
      setAmount('');
      setHorse(null);
      reload();
    }
  };
  const respond = async (b: any, accept: boolean) => {
    const res = await act('/api/sidebets/respond', { betId: b.id, accept, horse: answerHorse[b.id] });
    if (res) {
      if (accept) { play('chips'); fx.stamp('Bet on', `${money(b.amount)} each with ${b.other.name}`, 'gold'); } else play('flip');
      reload();
    }
  };
  const cancel = async (b: any) => {
    if (await act('/api/sidebets/cancel', { betId: b.id })) { play('chips'); reload(); }
  };
  const incoming = data.bets.filter((b: any) => b.status === 'pending' && !b.iChallenged);
  const outgoing = data.bets.filter((b: any) => b.status === 'pending' && b.iChallenged);
  const active = data.bets.filter((b: any) => b.status === 'active');
  const done = data.bets.filter((b: any) => b.status !== 'pending' && b.status !== 'active');

  return (
    <div>
      <CasinoNav game="sidebets" />
      <p className="muted">
        Put your money where your mouth is. Bet another player that your horse beats theirs in today’s race, or that you’ll out-earn, out-fight or out-work
        them by the end of the week. The house holds both stakes and pays the winner both. No cut for the house; ties give both stakes back.
      </p>

      {incoming.length > 0 && (
        <Panel title="Challenges for you">
          {incoming.map((b: any) => (
            <div key={b.id} className="bet-card incoming">
              <Portrait seed={b.other.portrait} playerId={b.other.id} size={44} />
              <div className="grow">
                <div><PlayerLink id={b.other.id} name={b.other.name} /> bets you <b className="money">{money(b.amount)}</b>{' '}
                  {b.kind === 'race' ? <>their horse <b>{b.theirHorse}</b> beats yours in today’s race.</> : <>they get more <b>{b.statLabel}</b> than you this week.</>}</div>
                <div className="small dim">Expires in {duration(Math.max(0, b.expiresAt - Date.now()))}. You need {money(b.amount)} cash on hand to accept.</div>
                {b.kind === 'race' && <HorsePicker horses={data.race.horses} value={answerHorse[b.id] ?? null} taken={b.theirPick} onChange={(i) => setAnswerHorse({ ...answerHorse, [b.id]: i })} />}
              </div>
              <div className="bet-actions">
                <button className={`btn green ${b.kind === 'race' && answerHorse[b.id] === undefined ? 'disabled' : ''}`} onClick={() => respond(b, true)}>Take the bet</button>
                <a className="small" onClick={() => respond(b, false)}>Turn it down</a>
              </div>
            </div>
          ))}
        </Panel>
      )}

      {active.length > 0 && (
        <Panel title="Bets in play">
          {active.map((b: any) => (
            <div key={b.id} className="bet-card">
              <Portrait seed={b.other.portrait} playerId={b.other.id} size={44} />
              <div className="grow">
                <div><b className="money">{money(b.amount * 2)}</b> pot with <PlayerLink id={b.other.id} name={b.other.name} /></div>
                {b.kind === 'race' ? (
                  <div className="small">Your <b>{b.myHorse}</b> vs. their <b>{b.theirHorse}</b>. Decided when the race runs at {new Date(data.race.postTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}. <a href="#/races">Watch the race</a></div>
                ) : b.progress && (
                  <div className="showdown">
                    <div className="small">Most {b.statLabel} · ends in {duration(Math.max(0, b.endsAt - Date.now()))}</div>
                    <ShowdownBar label="You" value={b.progress.me} other={b.progress.them} money={b.stat === 'cash_earned'} />
                    <ShowdownBar label={b.other.name} value={b.progress.them} other={b.progress.me} money={b.stat === 'cash_earned'} />
                  </div>
                )}
              </div>
            </div>
          ))}
        </Panel>
      )}

      <Panel title="Make a side bet">
        {others.length === 0 ? <div className="muted">There are no other real players yet. Side bets are between real players only.</div> : (
          <div className="sb-form">
            <label>Against{' '}
              <select value={opponent ?? ''} onChange={(e) => setOpponent(Number(e.target.value) || null)}>
                <option value="">pick a player…</option>
                {others.map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
            <div className="sb-kinds">
              <label className={kind === 'showdown' ? 'on' : ''}><input type="radio" checked={kind === 'showdown'} onChange={() => setKind('showdown')} /> <b>Weekly showdown</b><div className="small">Who gains more until the week ends (Monday midnight)?</div></label>
              <label className={kind === 'race' ? 'on' : ''}><input type="radio" checked={kind === 'race'} onChange={() => setKind('race')} /> <b>Horse vs. horse</b><div className="small">Each of you picks a horse in today’s race. The better finish wins.</div></label>
            </div>
            {kind === 'showdown' ? (
              <label>Most{' '}
                <select value={stat} onChange={(e) => setStat(e.target.value)}>
                  {Object.entries(data.stats).map(([k, v]: any) => <option key={k} value={k}>{v}</option>)}
                </select>
              </label>
            ) : raceSoon ? <div className="small bad">Today’s race is about to go off. Race bets open again for the next one.</div>
              : <HorsePicker horses={data.race.horses} value={horse} onChange={setHorse} />}
            <label>Stake $ <input value={amount} placeholder={String(data.minBet)} onChange={(e) => setAmount(e.target.value)} style={{ width: 110 }} /></label>
            <div className="small dim">From {money(data.minBet)} up to {money(rules.maxBetPerLevel)} × the lower of your two levels, paid from cash on hand. Your stake is held until they answer (up to {rules.expireHours} hours).</div>
            <button className={`btn big ${opponent && n >= data.minBet && n <= me.cash && (kind === 'showdown' || (horse !== null && !raceSoon)) ? '' : 'disabled'}`} onClick={challenge}>Send the challenge</button>
          </div>
        )}
      </Panel>

      {outgoing.length > 0 && (
        <Panel title="Waiting for an answer">
          {outgoing.map((b: any) => (
            <div key={b.id} className="feed-item" style={{ alignItems: 'center' }}>
              <span className="grow"><b className="money">{money(b.amount)}</b> to <PlayerLink id={b.other.id} name={b.other.name} />: {b.kind === 'race' ? <>your {b.myHorse} vs. their pick</> : <>most {b.statLabel}</>}
                <span className="small dim"> · expires in {duration(Math.max(0, b.expiresAt - Date.now()))}</span></span>
              <a className="small" onClick={() => cancel(b)}>call it off</a>
            </div>
          ))}
        </Panel>
      )}

      {done.length > 0 && (
        <Panel title="Settled this week">
          {done.map((b: any) => (
            <div key={b.id} className="feed-item">
              <span className="grow">
                {b.status === 'settled' ? (b.won === true ? <span className="sb-stamp good">Won</span> : b.won === false ? <span className="sb-stamp bad">Lost</span> : <span className="sb-stamp">Tie</span>)
                  : <span className="sb-stamp dim">{b.status === 'declined' ? 'Declined' : b.status === 'expired' ? 'Expired' : 'Called off'}</span>}
                {' '}{money(b.amount)} with <PlayerLink id={b.other.id} name={b.other.name} />{b.result?.summary && <span className="small dim"> · {b.result.summary}</span>}
              </span>
              {b.won === true && <span className="money">+{money(b.amount * 2)}</span>}
            </div>
          ))}
        </Panel>
      )}
    </div>
  );
}

function ShowdownBar({ label, value, other, money: isMoney }: { label: string; value: number; other: number; money: boolean }) {
  const top = Math.max(1, value, other);
  return (
    <div className={`showdown-row ${value > other ? 'ahead' : ''}`}>
      <span className="sd-name">{label}</span>
      <div className="progress grow"><div style={{ width: `${(Math.max(0, value) / top) * 100}%` }} /></div>
      <span className="sd-val">{isMoney ? money(value) : fmt(value)}</span>
    </div>
  );
}
